/**
 * Runtime error types used by the API layer.
 *
 * All network calls go through `api/client.ts`; failures surface as
 * `ApiError` so UI can narrow on status / problem-detail fields.
 */

export interface ProblemDetail {
  type: string
  title: string
  status: number
  detail?: string | null
  instance?: string | null
  request_id?: string | null
}

export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetail | null
  readonly data: unknown

  constructor(message: string, status: number, problem: ProblemDetail | null, data: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.data = data
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError
}

/**
 * Problem type do backend para "token recusado porque o relógio DO SERVIDOR
 * está atrasado" (``CLOCK_SKEW_PROBLEM_TYPE`` em ``_deps.py``). A sessão do
 * usuário está boa: não adianta mandá-lo fazer login de novo.
 */
export const CLOCK_SKEW_PROBLEM_TYPE = 'urn:prism:problem:clock-skew'

export const CLOCK_SKEW_MESSAGE =
  'O relógio do servidor do Prism está atrasado, então sua sessão não pôde ser confirmada. ' +
  'Não é um problema na sua conta: tente de novo em alguns minutos.'

export function isClockSkewError(err: unknown): boolean {
  return isApiError(err) && err.problem?.type === CLOCK_SKEW_PROBLEM_TYPE
}

/**
 * Problem types da troca de visibilidade no YouTube
 * (``web/api/v1/videos/routes.py``): o canal precisa reconectar (401) e a cota
 * da Data API acabou (429). O 401 daqui não é a sessão do Prism.
 */
export const YOUTUBE_REAUTH_PROBLEM_TYPE = 'urn:prism:problem:youtube-reauth'
export const YOUTUBE_QUOTA_PROBLEM_TYPE = 'urn:prism:problem:youtube-quota'

export function isYoutubeReauthError(err: unknown): boolean {
  return isApiError(err) && err.problem?.type === YOUTUBE_REAUTH_PROBLEM_TYPE
}

export function isYoutubeQuotaError(err: unknown): boolean {
  return isApiError(err) && err.problem?.type === YOUTUBE_QUOTA_PROBLEM_TYPE
}

/**
 * What kind of failure an error is, so the UI can tell "the server didn't
 * answer" apart from "the validation failed":
 *
 * - ``network``: the request never got a response (``ApiError`` status 0,
 *   or the browser's ``TypeError`` from a rejected ``fetch``).
 * - ``unavailable``: a gateway answered for the backend (502–504, 520–530)
 *   with a non-JSON body — the Cloudflare page while the tunnel or the
 *   backend is down. With a Problem Detail the backend itself answered, so
 *   it's ``server``.
 * - ``server``: any other 5xx.
 * - the 4xx get their own kinds; ``unknown`` is everything else (render
 *   bugs, aborted requests, our own thrown ``Error``s).
 */
export type ErrorKind =
  | 'network'
  | 'unavailable'
  | 'server'
  | 'auth'
  | 'forbidden'
  | 'not_found'
  | 'validation'
  | 'rate_limit'
  | 'clock_skew'
  | 'client'
  | 'unknown'

/** Messages each browser puts on the ``TypeError`` of a rejected ``fetch``
 * (Chrome, Firefox, Safari, Node). Any other ``TypeError`` is a bug. */
const FETCH_FAILURE_MESSAGE =
  /^(Failed to fetch|NetworkError when attempting to fetch resource\.?|Load failed|fetch failed)$/

/** ``fetch`` rejected because the request never got an answer. A ``TypeError``
 * for an invalid URL or header ("Failed to parse URL…", "Invalid name") is a
 * bug of ours, not the server being down. */
export function isFetchFailure(err: unknown): boolean {
  return err instanceof TypeError && FETCH_FAILURE_MESSAGE.test(err.message)
}

function isGatewayStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 530)
}

export function errorKind(err: unknown): ErrorKind {
  if (isClockSkewError(err)) return 'clock_skew'
  if (isApiError(err)) {
    const { status } = err
    if (status === 0) return 'network'
    if (status >= 500) return err.problem === null && isGatewayStatus(status) ? 'unavailable' : 'server'
    switch (status) {
      case 401:
        return 'auth'
      case 403:
        return 'forbidden'
      case 404:
        return 'not_found'
      case 422:
        return 'validation'
      case 429:
        return 'rate_limit'
      default:
        return status >= 400 ? 'client' : 'unknown'
    }
  }
  if (isFetchFailure(err)) return 'network'
  return 'unknown'
}

/** The server can't be reached right now (network or gateway page). */
export function isApiUnavailable(err: unknown): boolean {
  const kind = errorKind(err)
  return kind === 'network' || kind === 'unavailable'
}

export const API_UNAVAILABLE_MESSAGE =
  'Não foi possível falar com o servidor do Prism agora. Ele pode estar em manutenção — ' +
  'tente de novo em instantes.'

export const SERVER_ERROR_MESSAGE = 'Algo falhou no servidor do Prism. Tente de novo em instantes.'

export const SESSION_EXPIRED_MESSAGE = 'Sua sessão expirou. Entre de novo para continuar.'

const FORBIDDEN_MESSAGE = 'Sua conta não tem permissão para isso.'

const RATE_LIMIT_MESSAGE = 'Muitas requisições. Aguarde alguns segundos.'

/** ``detail`` of the backend's catch-all 500 handler (``middleware.py``):
 * English boilerplate, not something written for the user. */
const UNHANDLED_SERVER_DETAIL = /^An unexpected error occurred\b/

/**
 * Friendly single-line message for banners, toasts, and generic error spans.
 *
 * - network / unavailable → ``API_UNAVAILABLE_MESSAGE`` (never the runtime's
 *   "Failed to fetch" nor the gateway's ``statusText``);
 * - 5xx → the ``detail`` our backend wrote, else a generic server message;
 * - 401 / 403 → ``detail`` / ``title``, else a session / permission message;
 * - 429 → a "wait a bit" message;
 * - other 4xx → ``detail``, then ``title``, then the message the endpoint
 *   gave the ``ApiError`` ("Falha ao gerar thumbnail (HTTP 400)"), then
 *   ``fallback``. The client never puts ``statusText`` in that message, so
 *   an empty one means "nothing of our own to say".
 *
 * For 422 responses the server encodes the Pydantic error array as a JSON
 * string in ``detail``; we detect that and collapse it to a readable summary
 * ("2 campos inválidos") — callers that need per-field messages should go
 * through ``fieldErrors(err)`` instead.
 *
 * A plain ``Error`` keeps its message (the endpoints throw PT messages of
 * our own), except ``SyntaxError`` (bad JSON) and bug ``TypeError``s.
 */
export function errorMessage(err: unknown, fallback = 'Erro inesperado'): string {
  const kind = errorKind(err)
  switch (kind) {
    case 'clock_skew':
      return CLOCK_SKEW_MESSAGE
    case 'network':
    case 'unavailable':
      return API_UNAVAILABLE_MESSAGE
    case 'rate_limit':
      return RATE_LIMIT_MESSAGE
    default:
      break
  }
  if (isApiError(err)) {
    const problem = err.problem
    switch (kind) {
      case 'server': {
        const detail = problem?.detail
        return detail && !UNHANDLED_SERVER_DETAIL.test(detail) ? detail : SERVER_ERROR_MESSAGE
      }
      case 'auth':
        return problem?.detail ?? problem?.title ?? SESSION_EXPIRED_MESSAGE
      case 'forbidden':
        return problem?.detail ?? problem?.title ?? FORBIDDEN_MESSAGE
      case 'validation': {
        const entries = fieldErrors(err)
        const count = Object.keys(entries).length
        if (count === 0) return problem?.title ?? ownMessage(err, fallback)
        if (count === 1) {
          const only = Object.values(entries)[0]
          if (only) return only
        }
        return `${String(count)} campos inválidos`
      }
      default:
        return problem?.detail ?? problem?.title ?? ownMessage(err, fallback)
    }
  }
  if (err instanceof SyntaxError || err instanceof TypeError) return fallback
  if (err instanceof Error) return err.message !== '' ? err.message : fallback
  return fallback
}

/** The message an endpoint wrote into the ``ApiError`` (empty when the
 * response had nothing usable), else ``fallback``. */
function ownMessage(err: ApiError, fallback: string): string {
  return err.message !== '' ? err.message : fallback
}

const KIND_TITLES: Record<ErrorKind, string> = {
  network: 'Servidor indisponível',
  unavailable: 'Servidor indisponível',
  server: 'Erro no servidor',
  auth: 'Sessão expirada',
  forbidden: 'Acesso negado',
  not_found: 'Não encontrado',
  validation: 'Dados inválidos',
  rate_limit: 'Muitas requisições',
  clock_skew: 'Relógio do servidor atrasado',
  client: 'Algo deu errado',
  unknown: 'Algo deu errado',
}

/** Short heading for the error (panels, notices). */
export function errorTitle(err: unknown): string {
  return KIND_TITLES[errorKind(err)]
}

/**
 * Parses a 422 ProblemDetail into a ``{ fieldPath: message }`` map that
 * forms can consume directly. ``fieldPath`` drops the leading ``body``
 * segment so React Hook Form callers can say ``errors[name]`` without
 * prefixing. Non-422 errors return an empty map — the single-line
 * ``errorMessage`` is the right surface for those.
 */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!isApiError(err) || err.status !== 422) return {}
  const raw = err.problem?.detail
  if (typeof raw !== 'string') return {}
  const parsed = safeJsonParse(raw)
  if (!Array.isArray(parsed)) return {}

  const out: Record<string, string> = {}
  for (const item of parsed) {
    if (!item || typeof item !== 'object') continue
    const entry = item as Record<string, unknown>
    const loc = entry['loc']
    const msg = typeof entry['msg'] === 'string' ? entry['msg'] : null
    if (!Array.isArray(loc) || !msg) continue
    // Drop the "body"/"query"/"path" prefix the FastAPI validator adds so
    // callers can match against their own field names directly.
    const trimmed =
      loc[0] === 'body' || loc[0] === 'query' || loc[0] === 'path' ? loc.slice(1) : loc
    const key = trimmed.map((p) => String(p)).join('.')
    // First error wins per field — a Pydantic pass can produce several for
    // the same loc (e.g. type + value range), and the first is usually the
    // most actionable.
    if (key && !(key in out)) out[key] = humanizePydanticMessage(msg, entry)
  }
  return out
}

/**
 * Flattens a ProblemDetail + its parsed field errors into a single list of
 * ``{ field, message }`` entries. Useful for the FormErrorSummary banner
 * and for non-field errors (``field === null`` — top-level / server).
 */
export interface ErrorEntry {
  field: string | null
  message: string
}

export function errorEntries(err: unknown): ErrorEntry[] {
  if (!isApiError(err) || isClockSkewError(err)) {
    return [{ field: null, message: errorMessage(err) }]
  }
  if (err.status === 422) {
    const map = fieldErrors(err)
    const entries: ErrorEntry[] = []
    for (const [field, message] of Object.entries(map)) entries.push({ field, message })
    if (entries.length > 0) return entries
  }
  return [{ field: null, message: errorMessage(err) }]
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Pydantic's English messages are fine for devs but grating in a PT-BR UI.
 * We translate the most common ones here. Anything we don't recognize falls
 * through to the raw ``msg`` unchanged — that's still better than hiding it.
 */
function humanizePydanticMessage(msg: string, entry: Record<string, unknown>): string {
  const type = typeof entry['type'] === 'string' ? entry['type'] : ''
  switch (type) {
    case 'missing':
      return 'Campo obrigatório'
    case 'string_too_short':
      return 'Texto curto demais'
    case 'string_too_long':
      return 'Texto longo demais'
    case 'value_error':
      return msg.replace(/^Value error, /, '')
    case 'literal_error': {
      const ctx = entry['ctx']
      const expected =
        ctx &&
        typeof ctx === 'object' &&
        typeof (ctx as Record<string, unknown>)['expected'] === 'string'
          ? (ctx as Record<string, string>)['expected']
          : null
      return expected ? `Valor inválido. Esperado: ${expected}` : 'Valor inválido'
    }
    case 'greater_than':
    case 'greater_than_equal':
    case 'less_than':
    case 'less_than_equal':
      return msg
    default:
      return msg
  }
}
